import React, { useState } from "react";
import { useLang } from "@/lib/i18n-context";
import { GALLERY_YEARS } from "@/lib/gallery-data";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Lightbox modal shown when a photo is clicked
function Lightbox({ photo, onClose, onPrev, onNext, lang }) {
  const caption = photo?.caption?.[lang] || photo?.caption?.en || "";
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (!photo) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-md flex items-center justify-center text-white transition-colors"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-md flex items-center justify-center text-white transition-colors"
        data-testid="lightbox-next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-md flex items-center justify-center text-white transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-6xl max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.src}
          alt={caption}
          className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
        />
        {caption && (
          <div className="mt-4 text-white/90 text-sm md:text-base text-center max-w-2xl px-4">
            {caption}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GalleryPage() {
  const { t, lang } = useLang();
  const [lightbox, setLightbox] = useState(null); // { yearIdx, photoIdx }

  const allPhotos = GALLERY_YEARS.flatMap((y, yi) =>
    y.photos.map((p, pi) => ({ ...p, year: y.year, yi, pi }))
  );

  const openAt = (year, photoIdx) => {
    const flatIdx = allPhotos.findIndex((p) => p.year === year && p.pi === photoIdx);
    if (flatIdx >= 0) setLightbox(flatIdx);
  };

  const next = () => setLightbox((i) => (i + 1) % allPhotos.length);
  const prev = () => setLightbox((i) => (i - 1 + allPhotos.length) % allPhotos.length);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="mb-12 max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mb-3">Memories</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold" data-testid="gallery-title">
          IJFF Through the Years
        </h1>
        <p className="text-muted-foreground mt-4 text-lg">
          A look back at our previous Indonesia-Japan Friendship Festivals — the performers, the crowds,
          the food, the shared moments.
        </p>
      </div>

      {GALLERY_YEARS.map((year) => {
        const tagline = year.tagline?.[lang] || year.tagline?.en || "";
        const featured = year.photos.find((p) => p.featured) || year.photos[0];
        const rest = year.photos.filter((p) => p !== featured);
        return (
          <section key={year.year} className="mb-24" data-testid={`gallery-year-${year.year}`}>
            {/* Year header */}
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-red font-semibold">Edition</div>
                <div className="flex items-baseline gap-4">
                  <h2 className="font-display text-5xl md:text-7xl font-bold">
                    {year.year}
                  </h2>
                  {tagline && (
                    <div className="text-lg md:text-xl text-muted-foreground italic">
                      {tagline}
                    </div>
                  )}
                </div>
              </div>
              {year.stats && (
                <div className="flex gap-6 text-sm">
                  {year.stats.visitors && (
                    <div><div className="font-display text-2xl font-bold text-brand-red">{year.stats.visitors}</div><div className="text-muted-foreground">Visitors</div></div>
                  )}
                  {year.stats.booths && (
                    <div><div className="font-display text-2xl font-bold text-brand-red">{year.stats.booths}</div><div className="text-muted-foreground">Booths</div></div>
                  )}
                  {year.stats.performers && (
                    <div><div className="font-display text-2xl font-bold text-brand-red">{year.stats.performers}</div><div className="text-muted-foreground">Performers</div></div>
                  )}
                </div>
              )}
            </div>

            {/* Photos grid: featured big + rest */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
              {/* Featured hero */}
              <button
                onClick={() => openAt(year.year, year.photos.indexOf(featured))}
                className={`relative overflow-hidden rounded-3xl group ${
                  rest.length === 0 ? "md:col-span-12 aspect-[16/9]" :
                  rest.length <= 2 ? "md:col-span-8 aspect-[4/3]" :
                  "md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-auto"
                }`}
                data-testid={`gallery-featured-${year.year}`}
              >
                <img
                  src={featured.src}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white text-left">
                  <div className="text-xs uppercase tracking-widest text-white/80 mb-1">Featured · {year.year}</div>
                  <div className="font-display text-lg md:text-2xl font-bold text-shadow-hero">
                    {featured.caption?.[lang] || featured.caption?.en || ""}
                  </div>
                </div>
              </button>

              {/* Remaining photos */}
              {rest.map((p) => (
                <button
                  key={p.src}
                  onClick={() => openAt(year.year, year.photos.indexOf(p))}
                  className="relative overflow-hidden rounded-3xl group md:col-span-5 aspect-[4/3]"
                  data-testid={`gallery-photo-${year.year}-${year.photos.indexOf(p)}`}
                >
                  <img
                    src={p.src}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white text-left opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-xs font-medium text-shadow-hero">
                      {p.caption?.[lang] || p.caption?.en || ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <div className="bg-brand-canvas rounded-3xl p-10 md:p-14 text-center">
        <h3 className="font-display text-3xl md:text-4xl font-bold">
          Be part of the 2026 edition.
        </h3>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
          Register as a performer, restaurant, trader, business matching participant or sponsor —
          and make new memories at Shiokaze Park.
        </p>
      </div>

      <Lightbox
        photo={lightbox !== null ? allPhotos[lightbox] : null}
        onClose={() => setLightbox(null)}
        onPrev={prev}
        onNext={next}
        lang={lang}
      />
    </div>
  );
}
